
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type ReportKey =
  | "sales"
  | "purchase"
  | "salesBranchWise"
  | "purchaseBranchWise"
  | "headWiseSale"
  | "headWisePurchase"
  | "itemCategoryWiseSale"
  | "itemCategoryWisePurchase"
  | "accountCategoryWiseSale"
  | "accountCategoryWisePurchase"
  | "itemGroupWiseSale"
  | "itemGroupWisePurchase"
  | "accountGroupWiseSale"
  | "accountGroupWisePurchase";
  


// ─────────────────────────────────────────────
// NORMAL COMPARATIVE API TYPES
// ─────────────────────────────────────────────

export interface DashboardMRow {
  Particulars: string;
  UBold: number;
  LYAmount: number;
  CYAmount: number;
  GrowthPer: number;
}

export interface DashboardComparativeData {
  DataY: {
    LYAmount: number;
    CYAmount: number;
    GrowthPer: number;
  };

  DataM: DashboardMRow[];
}

// ─────────────────────────────────────────────
// BRANCH WISE API TYPES
// ─────────────────────────────────────────────

export interface BranchWiseMonthRow {
  Month: string;
  Bold: number;
  CYAmount: number;
  LYAmount: number;
  GrowthPer: number;
}

export interface BranchWiseRow {
  Particular: string;
  DataM: BranchWiseMonthRow[];
}

// ─────────────────────────────────────────────
// GENERIC API RESPONSE
// supports BOTH:
// 1. Normal comparative
// 2. Branch wise comparative
// ─────────────────────────────────────────────

export interface DashboardApiResponse {
  Status: number;
  Msg: string;

  Data:
    | DashboardComparativeData
    | BranchWiseRow[];
}

// ─────────────────────────────────────────────
// STATE
// ─────────────────────────────────────────────

interface ComparativeDashState {
  // dynamic report storage
  reportData: Record<string, any>;

  activeReportKey: string | null;

  yr1Label: string;
  yr2Label: string;

  loading: boolean;

  error: string | null;
}

const initialState: ComparativeDashState = {
  reportData: {},

  activeReportKey: null,

  yr1Label: "",
  yr2Label: "",

  loading: false,

  error: null,
};

// ─────────────────────────────────────────────
// SLICE
// ─────────────────────────────────────────────

const comparativeDashSlice = createSlice({
  name: "comparativeDash",

  initialState,

  reducers: {
    // active report
    setActiveReportKey(
      state,
      action: PayloadAction<string>
    ) {
      state.activeReportKey =
        action.payload;
    },

    // generic report setter
    setReportData(
      state,
      action: PayloadAction<{
        key: string;
        data: DashboardApiResponse;
      }>
    ) {
      const { key, data } =
        action.payload;

      if (
        data &&
        data.Data !== undefined
      ) {
        // save exactly as API gives
        state.reportData[key] =
          data.Data;
      }

      state.error = null;
      state.loading = false;
    },

    // loading
    setLoading(
      state,
      action: PayloadAction<boolean>
    ) {
      state.loading =
        action.payload;
    },

    // error
    setError(
      state,
      action: PayloadAction<
        string | null
      >
    ) {
      state.error =
        action.payload;

      state.loading = false;
    },

    // FY labels
    setYearLabels(
      state,
      action: PayloadAction<{
        yr1: string;
        yr2: string;
      }>
    ) {
      state.yr1Label =
        action.payload.yr1;

      state.yr2Label =
        action.payload.yr2;
    },
  },
});

export const {
  setActiveReportKey,
  setReportData,
  setLoading,
  setError,
  setYearLabels,
} = comparativeDashSlice.actions;

export default
  comparativeDashSlice.reducer;

